// Tipos para o novo sistema de Network Users

export enum NetworkUserRole {
  NETWORK_ADMIN = 'NETWORK_ADMIN',
  NETWORK_SUPERVISOR = 'NETWORK_SUPERVISOR',
  STORE_OWNER = 'STORE_OWNER',
  STORE_MANAGER = 'STORE_MANAGER',
  STORE_SELLER = 'STORE_SELLER',
  STORE_FINANCE = 'STORE_FINANCE',
  STORE_PREPARER = 'STORE_PREPARER',
  STORE_DOCS = 'STORE_DOCS',
  STORE_VIEWER = 'STORE_VIEWER',
  BROKER_PARTNER = 'BROKER_PARTNER',
}

// Alias para compatibilidade
export type NetworkRole = NetworkUserRole;
export const NetworkRole = NetworkUserRole;

export enum NetworkScopeType {
  ALL_STORES = 'ALL_STORES',
  STORE_SET = 'STORE_SET',
  SINGLE_STORE = 'SINGLE_STORE',
}

// Alias para compatibilidade
export type ScopeType = NetworkScopeType;
export const ScopeType = NetworkScopeType;

// StoreFeature unificado com NetworkModule + features extras (EQUIPE, CONFIGURACOES)
export enum StoreFeature {
  // Módulos de rede (1:1 com NetworkModule)
  INICIO = 'INICIO',
  DASHBOARD = 'DASHBOARD',
  ESTOQUE = 'ESTOQUE',
  VITRINE = 'VITRINE',
  PESSOAS = 'PESSOAS', // Renomeado de CLIENTES
  VENDAS = 'VENDAS',
  MARKETING = 'MARKETING',
  CRM = 'CRM',
  RESERVAS = 'RESERVAS',
  DOCUMENTACAO = 'DOCUMENTACAO',
  TAREFAS = 'TAREFAS',
  AUDITORIA = 'AUDITORIA',
  FORNECEDORES = 'FORNECEDORES',
  FINANCEIRO = 'FINANCEIRO',
  FERRAMENTAS = 'FERRAMENTAS',
  // Módulo de gerenciamento unificado
  GERENCIAR = 'GERENCIAR',
  // Features legadas (mantidas para compatibilidade com dados existentes)
  EQUIPE = 'EQUIPE',
  CONFIGURACOES = 'CONFIGURACOES',
  // Alias legado para compatibilidade
  /** @deprecated Use PESSOAS instead */
  CLIENTES = 'PESSOAS',
}

// Representa um acesso específico a uma loja com role e features independentes
export interface StoreAccess {
  roleId: string;           // ID do registro em user_network_roles
  storeId: string | null;   // ID da loja (null se ALL_STORES)
  storeIds: string[] | null; // Array de IDs das lojas
  storeName: string;        // Nome da loja (para display)
  storeNames: string[];     // Array de nomes das lojas
  role: NetworkUserRole;    // STORE_SELLER, STORE_MANAGER, etc.
  scopeType: NetworkScopeType; // Tipo de escopo
  features: StoreFeature[]; // Features permitidas nesta loja
  subFeatures: string[];    // Subfuncionalidades granulares
  isSeller: boolean;        // NOVO: Flag de vendedor independente da role
  isActive: boolean;        // Status deste acesso específico
  grantedAt: string;        // Data de concessão
  grantedBy: string | null; // Quem concedeu
}

export interface NetworkUserWithDetails {
  id: string;
  name: string;
  email: string;
  username: string | null;
  avatar_url: string | null;
  phone: string | null;
  is_active: boolean;
  is_master: boolean;
  is_platform_admin: boolean;
  
  // NOVO: Array de acessos por loja (cada acesso pode ter role/features diferentes)
  storeAccesses: StoreAccess[];
  
  // Campos computados para compatibilidade e exibição resumida
  primaryRole: NetworkUserRole | null;  // Role mais alto (primeiro acesso)
  allStoreNames: string[];              // Todas as lojas (união)
  
  // Campos legados para compatibilidade (derivados do primeiro acesso)
  networkRole: NetworkUserRole | null;
  scopeType: NetworkScopeType | null;
  storeIds: string[] | null;
  features: StoreFeature[] | null;
  roleGrantedBy: string | null;
  roleCreatedAt: string | null;
  roleIsActive: boolean;
  storeNames: string[];
  
  // Metadata
  sales: number | null;
  performance: number | null;
  network_id: string | null;
  created_at: string;
}

// Labels para exibição
export const NETWORK_ROLE_LABELS: Record<NetworkUserRole, string> = {
  [NetworkUserRole.NETWORK_ADMIN]: 'Admin de Rede',
  [NetworkUserRole.NETWORK_SUPERVISOR]: 'Supervisor de Rede',
  [NetworkUserRole.STORE_OWNER]: 'Proprietário',
  [NetworkUserRole.STORE_MANAGER]: 'Gerente',
  [NetworkUserRole.STORE_SELLER]: 'Vendedor',
  [NetworkUserRole.STORE_FINANCE]: 'Financeiro',
  [NetworkUserRole.STORE_PREPARER]: 'Preparador',
  [NetworkUserRole.STORE_DOCS]: 'Documentação',
  [NetworkUserRole.STORE_VIEWER]: 'Visualizador',
  [NetworkUserRole.BROKER_PARTNER]: 'Corretor Parceiro',
};

export const SCOPE_TYPE_LABELS: Record<NetworkScopeType, string> = {
  [NetworkScopeType.ALL_STORES]: 'Todas as Lojas',
  [NetworkScopeType.STORE_SET]: 'Conjunto de Lojas',
  [NetworkScopeType.SINGLE_STORE]: 'Loja Única',
};

// Features de módulos do sistema (ordem do menu principal - 9 módulos)
export const MODULE_FEATURES: StoreFeature[] = [
  StoreFeature.INICIO,       // 1. Início
  StoreFeature.DASHBOARD,    // 2. Painel (módulo independente)
  StoreFeature.ESTOQUE,      // 2. Estoque
  StoreFeature.VITRINE,      // 2b. Vitrine (módulo independente)
  StoreFeature.PESSOAS,      // 3. Pessoas (antigo Clientes)
  StoreFeature.VENDAS,       // 4. Vendas (Reservas, Documentação são subfunções)
  StoreFeature.MARKETING,    // 5. Marketing
  StoreFeature.CRM,          // 6. CRM
  StoreFeature.TAREFAS,      // 7. Tarefas
  StoreFeature.FINANCEIRO,   // 8. Financeiro
  StoreFeature.FERRAMENTAS,  // 9. Ferramentas (Fornecedores é subfunção)
  StoreFeature.GERENCIAR,    // 10. Gerenciar
];

// Módulos legados mantidos para compatibilidade (agora são subfunções)
export const LEGACY_MODULES: StoreFeature[] = [
  StoreFeature.RESERVAS,
  StoreFeature.DOCUMENTACAO,
  StoreFeature.FORNECEDORES, // Agora é subfunção de PESSOAS
  StoreFeature.AUDITORIA,
];

// @deprecated - Mantido para compatibilidade, usar MODULE_FEATURES
export const MANAGEMENT_FEATURES: StoreFeature[] = [];

export const FEATURE_LABELS: Record<StoreFeature, string> = {
  // Módulos principais do menu (10 módulos)
  [StoreFeature.INICIO]: 'Início',
  [StoreFeature.DASHBOARD]: 'Painel',
  [StoreFeature.ESTOQUE]: 'Estoque',
  [StoreFeature.VITRINE]: 'Vitrine',
  [StoreFeature.PESSOAS]: 'Pessoas',
  [StoreFeature.VENDAS]: 'Vendas',
  [StoreFeature.MARKETING]: 'Marketing',
  [StoreFeature.CRM]: 'CRM',
  [StoreFeature.TAREFAS]: 'Tarefas',
  [StoreFeature.FINANCEIRO]: 'Financeiro',
  [StoreFeature.FERRAMENTAS]: 'Ferramentas',
  [StoreFeature.GERENCIAR]: 'Gerenciar',
  // Módulos legados (agora subfunções, mantidos para compatibilidade)
  [StoreFeature.RESERVAS]: 'Reservas',
  [StoreFeature.DOCUMENTACAO]: 'Documentação',
  [StoreFeature.FORNECEDORES]: 'Fornecedores',
  [StoreFeature.AUDITORIA]: 'Auditoria',
  [StoreFeature.EQUIPE]: 'Gerenciar Equipe',
  [StoreFeature.CONFIGURACOES]: 'Configurações da Rede',
};
